import React, { useEffect } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';

interface TabsPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
  renderHidden?: boolean;
}

interface TabsProps {
  headers: string[] | React.ReactNode[];
  headerBadges?: any[];
  icons?: (React.ReactElement | undefined)[];
  disabled?: boolean[];
  hidden?: boolean[];
  slugs?: string[];
  useHash?: boolean; // If true, use hash navigation instead of pathname
  tabs: React.ReactNode[];
  initialSelected?: number | null;
}
interface TabsControlledProps {
  headers: string[];
  headerBadges?: string[];
  icons?: (React.ReactElement | undefined)[];
  disabled?: boolean[];
  hidden?: boolean[];
  renderHidden?: boolean[];
  tabs: React.ReactNode[];
  selected: number;
  handleChange: (selected: number) => void;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function CustomTabsPanel(props: TabsPanelProps) {
  const { children, value, index, renderHidden, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {(renderHidden || value === index) && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function renderTabLabel(header: React.ReactNode, badge?: React.ReactNode) {
  if (badge !== null && badge !== undefined) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <Box component="span">{header}</Box>
        <Badge content={badge} />
      </Box>
    );
  }

  return <Box component="span">{header}</Box>;
}

const Badge = ({ content }: { content: any }) => {
  const theme = useTheme();
  return (
    <div
      style={{
        display: 'flex',
        justifyItems: 'center',
        alignItems: 'center',
        justifyContent: 'center',
        height: '1.5em',
        width: '1.5em',
        borderRadius: '100%',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        fontSize: '0.8em',
        marginLeft: '0.5em',
        verticalAlign: 'middle',
      }}
    >
      {content}
    </div>
  );
};

/**
 * Controlled tabs component with badges and icons support.
 * Caller manages selected state via handleChange callback.
 */
export function CustomTabsControlled(props: TabsControlledProps) {
  const { headerBadges, headers, disabled, icons, tabs, selected, hidden, renderHidden, handleChange } = props;

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={selected} onChange={(event: React.SyntheticEvent, newValue: number) => handleChange(newValue)}>
          {headers.map(
            (h, index) =>
              (!hidden || !hidden[index]) && (
                <Tab
                  key={index}
                  value={index}
                  label={renderTabLabel(h, headerBadges?.[index])}
                  {...a11yProps(index)}
                  disabled={disabled && disabled[index]}
                  icon={icons && icons[index]}
                  iconPosition="start"
                />
              ),
          )}
        </Tabs>
      </Box>
      {tabs.map((c, index) =>
        disabled && disabled[index] ? (
          <React.Fragment key={index} />
        ) : (
          <CustomTabsPanel key={index} value={selected} index={index} renderHidden={renderHidden && renderHidden[index]}>
            {c}
          </CustomTabsPanel>
        ),
      )}
    </>
  );
}

/**
 * Uncontrolled tabs component with URL-based navigation.
 * Supports pathname-based (slugs) or hash-based (useHash) navigation.
 * Syncs tab selection with current route.
 */
export default function CustomTabs(props: TabsProps) {
  const { headerBadges, headers, disabled, icons, tabs, hidden, initialSelected, slugs, useHash } = props;

  const location = useLocation();
  const navigate = useNavigate();

  const _getValueFromLocation = () => {
    if (useHash) {
      // For hash-based tabs, check hash first
      if (location.hash && slugs) {
        const hashWithoutSymbol = location.hash.substring(1);
        const foundTab = slugs.findIndex(slug => slug === hashWithoutSymbol);
        if (foundTab >= 0) return foundTab;
      }
    } else {
      // For path-based tabs, check pathname
      const foundTab = (slugs || []).findIndex(slug => location.pathname.includes(slug));
      if (foundTab >= 0) return foundTab;
    }

    // Fallback to initial or first visible tab
    if (hidden && hidden.length > 0 && (initialSelected === null || initialSelected === undefined))
      return hidden.findIndex(h => !h) || initialSelected || 0;
    else return initialSelected || 0;
  };

  const [value, setValue] = React.useState(_getValueFromLocation());
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    const newValue = _getValueFromLocation();
    if (newValue !== value) setValue(newValue);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (slugs && slugs[value]) {
      const slug = slugs[value];
      const urlHasAnySlug = slugs.some(s => s && location.pathname.includes(s));

      if (useHash) {
        // Hash-based navigation for nested tabs
        // Only update if the hash is different
        const currentHash = location.hash.substring(1);
        if (currentHash !== slug) {
          navigate({ pathname: location.pathname, hash: `#${slug}` }, { replace: true });
        }
      } else if (slug.startsWith('/')) {
        // Absolute path navigation - preserve hash if it exists
        const targetUrl = location.hash ? `${slug}${location.hash}` : slug;
        if (location.pathname !== slug) {
          navigate(targetUrl, { replace: !urlHasAnySlug });
        }
      } else {
        // Relative path navigation - preserve hash if it exists
        const targetUrl = location.hash ? `${slug}${location.hash}` : slug;
        if (!location.pathname.includes(slug)) {
          navigate(targetUrl, { replace: !urlHasAnySlug });
        }
      }
    }
  }, [value]);

  return (
    <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange}>
          {headers.map(
            (h, index) =>
              (!hidden || !hidden[index]) && (
                <Tab
                  key={index}
                  value={index}
                  label={renderTabLabel(h, headerBadges?.[index])}
                  {...a11yProps(index)}
                  disabled={disabled && disabled[index]}
                  icon={icons && icons[index]}
                  iconPosition="start"
                />
              ),
          )}
        </Tabs>
      </Box>
      {tabs.map((c, index) =>
        disabled && disabled[index] ? (
          <React.Fragment key={index} />
        ) : (
          <CustomTabsPanel key={index} value={value} index={index}>
            {c}
          </CustomTabsPanel>
        ),
      )}
    </>
  );
}
